import {
  forwardRef,
  useControllableProp,
  useFormControlContext,
} from '@chakra-ui/react-v2';
import { useCallback, useState } from 'react';

import {
  EyeCrossedIcon,
  EyeOpenIcon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  type InputProps,
} from '@frontend/legacy-components';

export const sizeMap = new Map([
  ['sm', 'lg'],
  ['md', 'xl'],
  ['lg', '2xl'],
]);

export type PasswordInputProps = InputProps & {
  isFakePassword?: boolean;
  defaultShowValue?: boolean;
  showValue?: boolean;
  onToggleShow?: () => void;
};

export const PasswordInput = forwardRef<PasswordInputProps, 'input'>(
  (
    {
      onToggleShow,
      isFakePassword = false,
      defaultShowValue = false,
      showValue,
      size = 'md',
      ...restProps
    },
    ref,
  ) => {
    const [show, setShow] = useState<boolean>(defaultShowValue);
    const field = useFormControlContext();

    const [isShowControlled, finalShow] = useControllableProp(showValue, show);

    const handleClick = useCallback(() => {
      if (!isShowControlled) {
        setShow(!finalShow);
      }
      onToggleShow?.();
    }, [finalShow, isShowControlled, onToggleShow]);

    const passwordTextSize = sizeMap.get(size);

    // NOTE: realPasswordProps allows for browser/password managers to autofill, fakePasswordProps blocks this
    const realPasswordProps = {
      type: 'password',
      fontFamily: 'Verdana',
      fontSize: passwordTextSize,
      letterSpacing: 0.25,
    } as const satisfies InputProps;

    const fakePasswordProps = {
      fontFamily: 'Verdana',
      fontSize: passwordTextSize,
      letterSpacing: 0.25,
      sx: { '-webkit-text-security': 'disc' },
    } as const satisfies InputProps;

    return (
      <InputGroup size={size}>
        <Input
          autoComplete="off"
          data-fs-hide="true"
          type="text"
          name="code-protect-input"
          {...(finalShow
            ? null
            : isFakePassword
            ? fakePasswordProps
            : realPasswordProps)}
          ref={ref}
          {...restProps}
        />
        <InputRightElement>
          <IconButton
            type="button"
            size={size}
            onClick={handleClick}
            isDisabled={field.isDisabled || restProps.isDisabled}
          >
            {finalShow ? <EyeOpenIcon /> : <EyeCrossedIcon />}
          </IconButton>
        </InputRightElement>
      </InputGroup>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
